// Generated by TaroBase SDK Generator: 2026-07-18T20:30:00.103Z
// Collection: listings/$listingId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * A plastic recovery listing created by a household (or consolidated by an agent). Companies browse, accept, schedule, and complete these. Public read for homepage counters (total kg, values).
 */

export interface ListingsRequest {
  createdBy: AddressType;
  plasticType: string;
  estimatedKg: number | IncrementOperation | TokenAmount;
  estimatedValue: number | IncrementOperation | TokenAmount;
  photoUrl?: string;
  city: string;
  locationName: string;
  lat: string;
  lng: string;
  status: string;
  acceptedBy?: AddressType;
  scheduledFor?: number | TimeOperation | IncrementOperation | TokenAmount;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
  completedAt?: number | TimeOperation | IncrementOperation | TokenAmount;
  txSignature?: string;
}

export interface ListingsResponse {
  createdBy: string;
  plasticType: string;
  estimatedKg: number;
  estimatedValue: number;
  photoUrl?: string;
  city: string;
  locationName: string;
  lat: string;
  lng: string;
  status: string;
  acceptedBy?: string;
  scheduledFor?: number;
  createdAt: number;
  completedAt?: number;
  txSignature?: string;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Listings operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildListings(listingId: string, data: ListingsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `listings/${listingId}`, document: doc };
}

/**
 * Authenticated users only. createdBy must equal the caller (@user.address). plasticType is one of PET|HDPE|nylon|sachet|mixed; status typically starts at 'open'. createdAt is required Unix seconds. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setListings(listingId: string, data: ListingsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`listings/${listingId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Listings: ${formatError(error)}`);
    return false;
  }
}

export type ListingsRequestUpdate = Partial<ListingsRequest>;

/**
 * Build a Listings update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateListings(listingId: string, data: ListingsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `listings/${listingId}`, document: doc };
}

/**
 * Allowed for the listing owner (@data.createdBy) or the accepting company for non-completion updates (accepting an open listing, scheduling, etc.). Marking a listing 'completed' is restricted: only the accepting company (@data.acceptedBy == @user.address) may do it, the listing must not already be completed, txSignature must not have been set before and must be set now, and a matching onchain payment record must exist at payments/$listingId paid by the company to the household (createdBy) for exactly estimatedValue * 10000 USDC base units (cents -> micro-USDC). This makes completion impossible without a successful onchain USDC payment. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateListings(listingId: string, data: ListingsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`listings/${listingId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Listings: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read all listings and fields, so unauthenticated homepage visitors can aggregate total kg recovered and estimated values.
   (Get Single Item)
 */
export async function getListings(listingId: string): Promise<ListingsResponse | null> {
  try {
    const result = await get(`listings/${listingId}`);
    return result as ListingsResponse | null;
  } catch (error) {
    console.error(`Error getting Listings: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Listings document. (
  Read Operation Details: Anyone can read all listings and fields, so unauthenticated homepage visitors can aggregate total kg recovered and estimated values.
  )
 */
export function subscribeListings(callback: (data: ListingsResponse | null) => void, listingId: string): Promise<() => Promise<void>> {
  return subscribe(`listings/${listingId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as ListingsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Listings at path '${`listings/${listingId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Listings items from collection listings
 
  Read Operation Details: Anyone can read all listings and fields, so unauthenticated homepage visitors can aggregate total kg recovered and estimated values.
  
 */
export async function getManyListings(filter: string = ""): Promise<ListingsResponse[]> {
  try {
    const results = await get("listings", { prompt: filter });
    return Array.isArray(results) ? results as ListingsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Listings collection at path 'listings': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Listings collection at listings
 
  Read Operation Details: Anyone can read all listings and fields, so unauthenticated homepage visitors can aggregate total kg recovered and estimated values.
  
 */
export function subscribeManyListings(callback: (data: ListingsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("listings", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as ListingsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Listings collection at path '${"listings"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Listings items from collection listings
 
  Read Operation Details: Anyone can read all listings and fields, so unauthenticated homepage visitors can aggregate total kg recovered and estimated values.
  
 */
export async function getAllListings(filter: string = ""): Promise<ListingsResponse[]> {
  try {
    const results = await get("listings", { prompt: filter });
    return Array.isArray(results) ? results as ListingsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Listings collection at path 'listings': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Listings collection at listings
 
  Read Operation Details: Anyone can read all listings and fields, so unauthenticated homepage visitors can aggregate total kg recovered and estimated values.
  
 */
export function subscribeAllListings(callback: (data: ListingsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("listings", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as ListingsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Listings collection at path '${"listings"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Listings items in collection listings.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countListings(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("listings", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Listings at path 'listings': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Listings items in collection listings.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateListings(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("listings", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Listings at path 'listings': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the listing owner (@data.createdBy) may delete it.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteListings(listingId: string): Promise<boolean> {
  try {
    const succeeded = await set(`listings/${listingId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Listings: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Listings for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteListings(listingId: string): DocumentOperation {
  return { path: `listings/${listingId}`, document: null };
}

